import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  getSite, listTickRuns, listForms, listOpenIssues, listAllPermits,
  listAllTrainingRecords, listTrainingPeople, listPlant, listSubcontractors,
  listOrgDocuments, listSiteDocuments,
  type Site, type TickListRun, type FormRow, type OpenIssue, type PermitRow,
} from '../lib/db';

const DAY_MS = 86400000;

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  // Monday = 1, Sunday = 0 — back up to most recent Monday
  const day = out.getDay() || 7;
  if (day !== 1) out.setDate(out.getDate() - (day - 1));
  return out;
}

interface ExpiryEntry {
  what: string;
  who: string;
  when: string;
  daysAway: number;
  category: 'training' | 'plant' | 'subbie' | 'document';
}

export function FridayWrapUp() {
  const { siteId } = useParams<{ siteId: string }>();
  const { org } = useUserOrg();

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // This-week stats
  const [tickRuns, setTickRuns] = useState<TickListRun[]>([]);
  const [accidents, setAccidents] = useState<FormRow[]>([]);
  const [issues, setIssues] = useState<OpenIssue[]>([]);
  const [permits, setPermits] = useState<PermitRow[]>([]);

  // Next-30-day expiries
  const [expiries, setExpiries] = useState<ExpiryEntry[]>([]);

  useEffect(() => {
    if (!siteId || !org) return;
    setLoading(true);
    setError(null);

    const weekStart = startOfWeek(new Date());
    const weekStartIso = weekStart.toISOString();
    const in30Iso = new Date(Date.now() + 30 * DAY_MS).toISOString();
    const nowIso = new Date().toISOString();

    (async () => {
      try {
        const [
          s,
          ticks,
          allForms,
          allIssues,
          allPermits,
          training,
          people,
          plant,
          subbies,
          orgDocs,
          siteDocs,
        ] = await Promise.all([
          getSite(siteId),
          listTickRuns(siteId, undefined, 100),
          listForms(siteId, undefined, 200),
          listOpenIssues(siteId),
          listAllPermits(siteId, 100),
          listAllTrainingRecords(org.org_id),
          listTrainingPeople(org.org_id),
          listPlant(org.org_id),
          listSubcontractors(org.org_id),
          listOrgDocuments(org.org_id),
          listSiteDocuments(siteId),
        ]);

        setSite(s);
        // Filter tick runs / forms / permits to this week
        setTickRuns(ticks.filter((r) => r.created_at >= weekStartIso));
        setAccidents(allForms.filter((f) => f.kind === 'accident' && f.created_at >= weekStartIso));
        setIssues(allIssues);
        setPermits(allPermits.filter((p) => p.issued_at >= weekStartIso));

        // Next-30-day expiries
        const exps: ExpiryEntry[] = [];
        // Training cards
        const peopleById = new Map(people.map((p) => [p.id, p.full_name]));
        for (const t of training) {
          if (!t.expires_on) continue;
          if (t.expires_on >= nowIso.slice(0, 10) && t.expires_on <= in30Iso.slice(0, 10)) {
            const days = Math.floor((new Date(t.expires_on).getTime() - Date.now()) / DAY_MS);
            exps.push({
              what: t.card_type ?? 'Training card',
              who: peopleById.get(t.person_id) ?? 'Unknown',
              when: fmtDate(t.expires_on),
              daysAway: days,
              category: 'training',
            });
          }
        }
        // Plant LOLER + PAT (last_done + 1 year)
        for (const p of plant) {
          for (const [field, label] of [['loler_date', 'LOLER'], ['pat_date', 'PAT']] as const) {
            const last = p[field];
            if (!last) continue;
            const due = new Date(last); due.setFullYear(due.getFullYear() + 1);
            if (due.getTime() < Date.now()) continue;
            if (due.getTime() <= Date.now() + 30 * DAY_MS) {
              const days = Math.floor((due.getTime() - Date.now()) / DAY_MS);
              exps.push({
                what: `${label} due`,
                who: p.name,
                when: fmtDate(due.toISOString()),
                daysAway: days,
                category: 'plant',
              });
            }
          }
        }
        // Subbie insurances
        for (const sub of subbies) {
          for (const [field, label] of [['pl_insurance_expiry', 'Public Liability insurance'], ['el_insurance_expiry', 'Employers Liability insurance']] as const) {
            const v = sub[field];
            if (!v) continue;
            const t = new Date(v).getTime();
            if (t < Date.now()) continue;
            if (t <= Date.now() + 30 * DAY_MS) {
              const days = Math.floor((t - Date.now()) / DAY_MS);
              exps.push({
                what: label,
                who: sub.company_name,
                when: fmtDate(v),
                daysAway: days,
                category: 'subbie',
              });
            }
          }
        }
        // Documents (e.g. insurance certs uploaded as docs)
        for (const d of [...orgDocs, ...siteDocs]) {
          if (!d.expires_at) continue;
          const t = new Date(d.expires_at).getTime();
          if (t < Date.now()) continue;
          if (t <= Date.now() + 30 * DAY_MS) {
            const days = Math.floor((t - Date.now()) / DAY_MS);
            exps.push({
              what: d.title,
              who: d.scope === 'site' ? 'Site doc' : 'Org doc',
              when: fmtDate(d.expires_at),
              daysAway: days,
              category: 'document',
            });
          }
        }
        exps.sort((a, b) => a.daysAway - b.daysAway);
        setExpiries(exps);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, org]);

  if (loading) return <AppShell title="Weekly review" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  if (!site) return <AppShell title="Weekly review" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;

  const passCount = tickRuns.filter((r) => Object.values(r.responses).every((x) => x?.state !== 'fail')).length;
  const totalChecks = tickRuns.length;

  return (
    <AppShell title="Weekly review" back={`/sites/${site.id}`}>
      <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)', borderColor: 'var(--brand-light)' }}>
        <p style={{ margin: 0 }}>
          <strong>{site.name}</strong> · week of {fmtDate(startOfWeek(new Date()).toISOString())}
        </p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
          A roll-up of what's happened this week and what's coming next.
        </p>
      </div>

      <Link to={`/sites/${siteId}/timesheet`} className="card tile-link" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px' }}>Hours / sign-in log →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Hours by person, ready for wages. Email or download as CSV / PDF.</p>
      </Link>

      {error && <div className="banner banner-error">{error}</div>}

      {/* ===== This week ===== */}
      <h3 className="section-title" style={{ marginTop: 0 }}>This week</h3>

      <div className="wrap-grid">
        <div className="wrap-stat">
          <span className="wrap-stat-num">{totalChecks}</span>
          <span className="wrap-stat-label">Checks logged</span>
          <span className="wrap-stat-sub">{passCount} clean · {totalChecks - passCount} with issues</span>
        </div>
        <div className="wrap-stat">
          <span className="wrap-stat-num">{permits.length}</span>
          <span className="wrap-stat-label">Permits issued</span>
        </div>
        <div className={`wrap-stat ${accidents.length > 0 ? 'wrap-stat-danger' : ''}`}>
          <span className="wrap-stat-num">{accidents.length}</span>
          <span className="wrap-stat-label">Incidents</span>
        </div>
        <div className={`wrap-stat ${issues.length > 0 ? 'wrap-stat-warn' : ''}`}>
          <span className="wrap-stat-num">{issues.length}</span>
          <span className="wrap-stat-label">Open issues</span>
        </div>
      </div>

      {issues.length > 0 && (
        <>
          <h4 style={{ margin: '16px 0 8px', fontSize: 14, color: 'var(--ink-soft)' }}>
            Outstanding from tick lists ({issues.length})
          </h4>
          <div className="doc-list" style={{ marginBottom: 12 }}>
            {issues.slice(0, 5).map((iss) => (
              <div key={`${iss.run_id}-${iss.item_key}`} className="card" style={{ marginBottom: 6 }}>
                <p style={{ margin: 0, fontSize: 13 }}>
                  <strong>{iss.item_key}</strong>
                  <span className="muted"> · {iss.age_days} days old</span>
                </p>
                {iss.notes && <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>{iss.notes}</p>}
              </div>
            ))}
            {issues.length > 5 && (
              <Link to={`/sites/${site.id}/issues`} className="btn btn-ghost btn-sm">
                View all {issues.length} →
              </Link>
            )}
          </div>
        </>
      )}

      {accidents.length > 0 && (
        <>
          <h4 style={{ margin: '16px 0 8px', fontSize: 14, color: 'var(--ink-soft)' }}>
            Incidents this week ({accidents.length})
          </h4>
          <div className="doc-list">
            {accidents.map((f) => (
              <div key={f.id} className="card" style={{ marginBottom: 6 }}>
                <p style={{ margin: 0, fontSize: 13 }}>
                  <strong>{f.title}</strong>
                </p>
                <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                  {fmtDate(f.created_at)}
                  {f.is_riddor && ' · RIDDOR-reportable'}
                </p>
                <Link to={`/forms/${f.id}`} className="btn-link-sm">View →</Link>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===== Next 30 days ===== */}
      <h3 className="section-title" style={{ marginTop: 24 }}>Next 30 days — expiries</h3>

      {expiries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Nothing expiring in the next 30 days. 🎉</p>
        </div>
      ) : (
        <div className="doc-list">
          {expiries.map((e, i) => (
            <div key={i} className="card" style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 13 }}>
                    <strong>{e.what}</strong>
                  </p>
                  <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                    {e.who} · expires {e.when}
                  </p>
                </div>
                <span className={`status-pill ${e.daysAway <= 7 ? 'status-danger' : 'status-warn'}`}>
                  {e.daysAway === 0 ? 'today' : `${e.daysAway}d`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <Link to="/registers/expiries" className="btn btn-ghost">View full expiries register →</Link>
      </div>
    </AppShell>
  );
}
